import { EventObject, Typestate } from 'xstate';
import { Role } from '../../types/role';
import { launchPageTypes } from '../../types/launchPageTypes';
import { NetworkError } from '../actions/genericActions';
import { ApplyRequestMachineContext, ApplyResponseMachineContext } from './shared/context/applyMachineContext';
import { BankDetailsMachineContext } from './shared/context/bankDetailsMachineContext';
import { CellPhoneMachineContext } from './shared/context/cellPhoneMachineContext';
import { CustomerSearchRequestMachineContext, CustomerSearchResponseMachineContext } from './shared/context/customerSearchMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { LeaseItem, SelectLeaseOptionErrors } from '../../gql/graphql';
import { CartItemModel } from './existingApplicationMachine.types';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { RoleMachineContext } from './shared/context/roleMachineContext';

export interface EcommMachineMachineContext
  // needs these types to pass them between machines
  extends RoleMachineContext,
    OrderMachineContext,
    OrderIdMachineContext,
    CellPhoneMachineContext,
    ApplyRequestMachineContext,
    ApplyResponseMachineContext,
    CustomerSearchRequestMachineContext,
    CustomerSearchResponseMachineContext,
    BankDetailsMachineContext {
  // configs
  highestSupportedApplicationMilestone: number;
  launchPage: launchPageTypes | null;
  reviewPage: boolean;
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;

  // used to transfer state between machines
  customerLookupStartingPage?: string;
  existingApplicationStartingPage?: string;
  // forwarded to post-decision-machine as skipToPage. Set only where the status alone would route
  // somewhere else (see post-decision-machine Entry); otherwise left undefined so it derives.
  postDecisionStartingPage?: string;
  longFormApplicationStartingPage?: string;
  entryPointPage: string;
  otbInitialState?: string;

  // used to transfer data between machines, other data fields are covered by the above MachineContexts
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  cart: CartItemModel[];

  didSkipOTP: boolean;

  // used to transfer errors to the error boundary
  networkError: NetworkError | null;

  validatedByTokenType?: ValidatedByTokenType;

  // Which kind of emailed link expired, set when EntryPointV2 lands on ExpiredLink
  expiredLinkType?: ExpiredLinkType;

  ldClient: LDClient;

  applicationItems: LeaseItem[]; // for m4 bandaid

  //TODO: Ford remove when we can get items from Application
  hasAppItems?: boolean;
  entrypointParam: string;
  entrypointSessionId?: string;
}

export enum EcommMachineStates {
  EntryPointV2 = 'EntryPointV2',
  EcommMachine = 'EcommMachine',
  Loading = 'Loading',
  EntryPoint = 'EntryPoint',
  ExistingApplication = 'ExistingApplication',
  CustomerLookupFlow = 'CustomerLookupFlow',
  VirtualCard = 'VirtualCard',
  NewApplication = 'NewApplication',
  AnotherTestState = 'AnotherTestState',
  OTB = 'OTB',
  Signed = 'Signed',
  Declined = 'Declined',
  LeaseDeclined = 'LeaseDeclined',
  LongFormApplication = 'LongFormApplication',
  PostDecision = 'PostDecision',
}

export interface EcommMachineMachineStatesSchema {
  [EcommMachineStates.EntryPointV2]: {};
  [EcommMachineStates.EcommMachine]: {};
  [EcommMachineStates.Loading]: {};
  [EcommMachineStates.ExistingApplication]: {};
  [EcommMachineStates.CustomerLookupFlow]: {};
  [EcommMachineStates.VirtualCard]: {};
  [EcommMachineStates.OTB]: {};
  [EcommMachineStates.NewApplication]: {};
  [EcommMachineStates.AnotherTestState]: {};
  [EcommMachineStates.Signed]: {};
  [EcommMachineStates.Declined]: {};
  [EcommMachineStates.LeaseDeclined]: {};
  [EcommMachineStates.LongFormApplication]: {};
  [EcommMachineStates.PostDecision]: {};
}

export interface EcommMachineMachineStates extends Typestate<EcommMachineMachineContext> {
  type: EcommMachineMachineStatesSchema;
}

export enum EcommMachineEvents {
  APP_IN_PROGRESS = 'AppInProgress',
  APP_FINISHED = 'AppFinished',
  VIRTUAL_CARD_APP = 'VirtualCardApp',
  CUSTOMER_LOOKUP_EVENT = 'CustomerLookupEvent',
  SKIP_OTP_EVENT = 'SkipOtpEvent',
  NEW_APPLICATION = 'NewApplication',
  EXISTING_APPLICATION = 'ExistingApplication',
  OTB_CUSTOMER = 'OtbCustomer',
  LONG_FORM = 'LongForm',
  // a submachine handing off to post-decision-machine; data is its context plus postDecisionStartingPage
  POST_DECISION = 'PostDecision',
  // post-decision-machine handing the debit card update back to the application machines
  EDIT_DEBIT_INFO = 'EditDebitInfo',
  NEXT = 'NEXT',
  BACK = 'BACK',
  DECLINE = 'Decline',
  ERROR = 'Error',
  PAYMENT_ERROR = 'PaymentError',
  NETWORK_ERROR = 'NETWORK_ERROR',
  PHONE_SUBMITTED = 'PHONE_SUBMITTED',
  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',
  RESTART_APPLICATION = 'RESTART_APPLICATION',
}

type EcommMachineEventsTypeSchema =
  | EcommMachineEvents.APP_IN_PROGRESS
  | EcommMachineEvents.APP_FINISHED
  | EcommMachineEvents.VIRTUAL_CARD_APP
  | EcommMachineEvents.CUSTOMER_LOOKUP_EVENT
  | EcommMachineEvents.SKIP_OTP_EVENT
  | EcommMachineEvents.NEW_APPLICATION
  | EcommMachineEvents.EXISTING_APPLICATION
  | EcommMachineEvents.LONG_FORM
  | EcommMachineEvents.POST_DECISION
  | EcommMachineEvents.EDIT_DEBIT_INFO
  | EcommMachineEvents.NEXT
  | EcommMachineEvents.BACK
  | EcommMachineEvents.DECLINE
  | EcommMachineEvents.ERROR
  | EcommMachineEvents.OTB_CUSTOMER
  | EcommMachineEvents.PAYMENT_ERROR
  | EcommMachineEvents.NETWORK_ERROR
  | EcommMachineEvents.PHONE_SUBMITTED
  | EcommMachineEvents.TRACK
  | EcommMachineEvents.PAGE
  | EcommMachineEvents.IDENTIFY
  | EcommMachineEvents.RESTART_APPLICATION;

export interface EcommMachineMachineEvents extends EventObject {
  type: EcommMachineEventsTypeSchema;
  data?: any;
}

// export enum EcommMachineGuardsType {
//   EXISTING_CUSTOMER_FOUND = 'ExistingCustomerFound',
//   CUSTOMER_OTB = 'CustomerOTB',
//   CUSTOMER_DECLINED = 'CustomerDeclined',
//   CUSTOMER_NOT_FOUND = 'CustomerNotFound',
// }

// type EcommMachineGuardsTypeSchema =
//   | EcommMachineGuardsType.EXISTING_CUSTOMER_FOUND
//   | EcommMachineGuardsType.CUSTOMER_OTB
//   | EcommMachineGuardsType.CUSTOMER_NOT_FOUND
//   | EcommMachineGuardsType.CUSTOMER_DECLINED;

// export interface EcommMachineMachineGuards extends EventObject {
//   type: EcommMachineGuardsTypeSchema;
// }

// Moving this out of redux, putting information shared by multiple machines here
export interface BasicInfoModel {
  firstName: string | undefined;
  lastName: string | undefined;
  emailAddress: string | null | undefined;
}
export interface BasicInfoContinuedModel {
  birthDate: string | undefined;
  taxpayerIdentificationNumber?: string;
  tokenizedTaxId?: string;
  monthlyIncome?: string;
}

export enum ValidatedByTokenType {
  HYBRID = 'HYBRID',
  OOBA = 'OOBA',
  CONTRACT = 'CONTRACT',
}

export enum ExpiredLinkType {
  OOBA = 'OOBA',
  CONTRACT = 'CONTRACT',
}
